import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useRef, useEffect } from "react";
import { LoginFlowData, LoginFlowSteps } from "@boundary/pages/3.login/loginFlow";
import { OnboardingElement } from "@boundary/widgets/onboarding";
import { Field, Form } from "@boundary/components/form";
import { Page } from "@boundary/layouts/pageLayout";
import { Button } from "@boundary/components/button";
import { Input } from "@boundary/components/input";
import { useAuth } from "@boundary/_hooks";
import styles from "./name.module.scss";

export const NameStep: OnboardingElement<LoginFlowData, LoginFlowSteps> = (props) => {
  const { auth, updateUserInfo } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const form = useForm<LoginFlowData>({
    resolver: zodResolver(
      LoginFlowData.pick({
        name: true
      })
    ),
    defaultValues: props.data
  });

  // Фокусируемся на поле ввода при монтировании компонента
  useEffect(() => {
    // Небольшая задержка, чтобы убедиться, что DOM готов
    const timer = setTimeout(() => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = form.handleSubmit(async (values: Pick<LoginFlowData, "name">) => {
    if (auth.state === "authenticated") {
      await updateUserInfo({
        name: values.name
      });
    }

    return props.onComplete(null);
  });

  return (
    <Page screenBackground="domain">
      <Page.SafeArea />
      <Page.DisplayZone>
        <Form {...form}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <Page.DisplayZone.Body className={styles.body}>
              <h1>Как вас зовут?</h1>
              <Field
                control={form.control}
                name="name"
                component={(fieldProps) => (
                  <Input
                    {...fieldProps}
                    ref={(e) => {
                      inputRef.current = e;
                      fieldProps.ref(e);
                    }}
                    placeholder="Имя"
                    autoFocus
                    onClick={(e) => {
                      // Предотвращаем потерю фокуса при первом клике
                      e.stopPropagation();
                      // Убеждаемся, что фокус установлен после клика
                      if (inputRef.current && document.activeElement !== inputRef.current) {
                        setTimeout(() => {
                          inputRef.current?.focus();
                        }, 0);
                      }
                    }}
                  />
                )}
              />
            </Page.DisplayZone.Body>
            <Page.DisplayZone.Sticky className={styles.buttonPad}>
              <Button disabled={form.formState.isSubmitting}>
                Завершить регистрацию
              </Button>
            </Page.DisplayZone.Sticky>
          </form>
        </Form>
      </Page.DisplayZone>
    </Page>
  );
};
